import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { providers, auth, categories } from '../services/api';
import GoogleReviewLinkFinder from './GoogleReviewLinkFinder';
import Navigation from './Navigation';

function Dashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState({
    // Basic Information
    business_name: '',
    contact_person: '',
    phone: '',
    whatsapp_number: '',
    alternative_phone: '',
    email_public: '',
    website: '',
    description: '',

    // Publishing Status
    is_published: false,

    // Categories
    primary_category_id: '',
    additional_categories: [],

    // Location
    city: '',
    province: '',
    postal_code: '',
    business_address: '',
    additional_cities: [],
    willing_to_travel: false,
    radius_km: '',

    // Business Details
    established_year: '',
    team_size: '',
    business_registration: '',
    years_experience: '',
    events_completed: '',

    // Pricing
    price_range: '',
    price_from: '',
    price_to: '',
    deposit_required: false,
    deposit_type: 'percentage',
    deposit_value: '',
    deposit_percentage: '', // Legacy field - keep for backwards compatibility
    payment_methods: [],

    // Services
    services_offered: [],
    specialties: [],
    languages_spoken: [],

    // Social Media & Reviews
    google_review_url: '',
    facebook_review_url: '',
    facebook_profile_url: '',
    instagram_url: '',
    youtube_url: '',
    tiktok_url: '',
    linkedin_url: '',
    pinterest_url: '',

    // Operating Details
    booking_lead_time: '',
    cancellation_policy: '',
    offers_consultation: false,
    consultation_fee: '',
    has_insurance: false,
    insurance_details: '',
    business_hours: '',

    // Additional
    awards_certifications: [],
    keywords: []
  });
  const [activeTab, setActiveTab] = useState('basic');
  const [categoryList, setCategoryList] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [additionalCategoryPairs, setAdditionalCategoryPairs] = useState([]);
  const [additionalCities, setAdditionalCities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [profileLoading, setProfileLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [showGoogleFinder, setShowGoogleFinder] = useState(false);

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (!storedUser) {
      navigate('/login');
      return;
    }
    const userData = JSON.parse(storedUser);
    setUser(userData);

    // Load categories first, then profile
    loadCategoriesAndProfile();
  }, [navigate]);

  const loadCategoriesAndProfile = async () => {
    try {
      // Load categories first
      const response = await categories.getAll();
      const cats = response.data?.categories || response.data || [];
      // Sort categories alphabetically by name
      const sortedCats = cats.sort((a, b) => a.name.localeCompare(b.name));
      console.log('Loaded categories:', sortedCats);
      setCategoryList(sortedCats);

      // Then load profile
      await loadProfile(sortedCats);
    } catch (error) {
      console.error('Failed to load categories:', error);
      setProfileLoading(false);
    }
  };

  const loadProfile = async (cats) => {
    setProfileLoading(true);

    try {
      // Always fetch from API to get latest data
      const response = await providers.getProfile();
      const data = response.data;

      // Update localStorage with fresh data
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const updatedUser = {
          ...JSON.parse(storedUser),
          ...data
        };
        localStorage.setItem('user', JSON.stringify(updatedUser));
        setUser(updatedUser);
      }

      setProfile({
        // Basic Information
        business_name: data.business_name || '',
        contact_person: data.contact_person || '',
        phone: data.phone || '',
        whatsapp_number: data.whatsapp_number || '',
        alternative_phone: data.alternative_phone || '',
        email_public: data.email_public || '',
        website: data.website || '',
        description: data.description || '',

        // Publishing Status
        is_published: data.is_published || false,

        // Categories
        primary_category_id: data.primary_category_id || '',
        additional_categories: data.additional_categories || [],

        // Location
        city: data.city || '',
        province: data.province || '',
        postal_code: data.postal_code || '',
        business_address: data.business_address || '',
        additional_cities: data.additional_cities || [],
        willing_to_travel: data.willing_to_travel === true,
        radius_km: data.radius_km || '',

        // Business Details
        established_year: data.established_year || '',
        team_size: data.team_size || '',
        business_registration: data.business_registration || '',
        years_experience: data.years_experience || '',
        events_completed: data.events_completed || '',

        // Pricing
        price_range: data.price_range || '',
        price_from: data.price_from || '',
        price_to: data.price_to || '',
        deposit_required: data.deposit_required || false,
        deposit_type: data.deposit_type || 'percentage',
        deposit_value: data.deposit_value || '',
        deposit_percentage: data.deposit_percentage || '',
        payment_methods: data.payment_methods || [],

        // Services
        services_offered: data.services_offered || [],
        specialties: data.specialties || [],
        languages_spoken: data.languages_spoken || [],

        // Social Media & Reviews
        google_review_url: data.google_review_url || '',
        facebook_review_url: data.facebook_review_url || '',
        facebook_profile_url: data.facebook_profile_url || '',
        instagram_url: data.instagram_url || '',
        youtube_url: data.youtube_url || '',
        tiktok_url: data.tiktok_url || '',
        linkedin_url: data.linkedin_url || '',
        pinterest_url: data.pinterest_url || '',

        // Operating Details
        booking_lead_time: data.booking_lead_time || '',
        cancellation_policy: data.cancellation_policy || '',
        offers_consultation: data.offers_consultation || false,
        consultation_fee: data.consultation_fee || '',
        has_insurance: data.has_insurance || false,
        insurance_details: data.insurance_details || '',
        business_hours: data.business_hours || '',

        // Additional
        awards_certifications: data.awards_certifications || [],
        keywords: data.keywords || []
      });

      // Set selected category for subcategory dropdown
      if (data.primary_category_id && cats && cats.length > 0) {
        const selected = cats.find(cat => cat.id === data.primary_category_id);
        setSelectedCategory(selected);
      }

      // Set additional categories directly
      if (data.additional_categories && data.additional_categories.length > 0) {
        setAdditionalCategoryPairs(data.additional_categories.map(catId => ({ category_id: catId })));
      }

      // Load additional cities
      if (data.additional_cities && Array.isArray(data.additional_cities)) {
        setAdditionalCities(data.additional_cities);
      }

    } catch (error) {
      console.error('Failed to load profile:', error);
      // If profile fetch fails, use whatever we have in localStorage
      const storedUser = JSON.parse(localStorage.getItem('user'));
      setProfile({
        business_name: storedUser?.business_name || '',
        contact_person: storedUser?.contact_person || '',
        phone: storedUser?.phone || '',
        primary_category_id: storedUser?.primary_category_id || '',
        additional_categories: storedUser?.additional_categories || [],
        google_review_url: storedUser?.google_review_url || '',
        facebook_review_url: storedUser?.facebook_review_url || '',
        city: storedUser?.city || '',
        province: storedUser?.province || '',
        postal_code: storedUser?.postal_code || '',
        willing_to_travel: storedUser?.willing_to_travel === true,
        radius_km: storedUser?.radius_km || ''
      });
    } finally {
      setProfileLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setProfile(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setLoading(true);
    setMessage('');

    try {
      // Convert additionalCategoryPairs to the format the API expects
      const additional_categories = additionalCategoryPairs.map(pair => pair.category_id);

      // Filter out empty string values to avoid overwriting existing data
      const cleanedProfile = {};
      Object.keys(profile).forEach(key => {
        const value = profile[key];
        // Include the value if it's not an empty string, or if it's a boolean/number/array
        if (value !== '' || typeof value === 'boolean' || typeof value === 'number' || Array.isArray(value)) {
          cleanedProfile[key] = value;
        }
      });

      const dataToSubmit = {
        ...cleanedProfile,
        additional_categories,
        additional_cities: additionalCities.filter(city => city.trim() !== '')
      };

      const response = await providers.updateProfile(dataToSubmit);
      setMessage('Profile updated successfully!');

      // Reload the full profile from API to ensure all fields are populated
      await loadProfile(categoryList);
    } catch (error) {
      setMessage('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    auth.logout();
    navigate('/');
  };

  const styles = {
    container: {
      maxWidth: '800px',
      margin: '20px auto',
      padding: '20px'
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '30px',
      padding: '20px',
      backgroundColor: '#f8f9fa',
      borderRadius: '8px'
    },
    title: {
      margin: 0,
      color: '#333'
    },
    logoutButton: {
      padding: '8px 16px',
      backgroundColor: '#dc3545',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer'
    },
    section: {
      backgroundColor: 'white',
      padding: '20px',
      borderRadius: '8px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      marginBottom: '20px'
    },
    sectionTitle: {
      marginBottom: '20px',
      color: '#333'
    },
    form: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '15px'
    },
    tabContainer: {
      display: 'flex',
      gap: '10px',
      marginBottom: '30px',
      gridColumn: 'span 2',
      borderBottom: '2px solid #e0e0e0',
      paddingBottom: '0'
    },
    tab: {
      padding: '12px 20px',
      border: 'none',
      backgroundColor: 'transparent',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '500',
      color: '#666',
      borderBottom: '3px solid transparent',
      transition: 'all 0.3s',
      marginBottom: '-2px'
    },
    activeTab: {
      color: '#007bff',
      borderBottom: '3px solid #007bff'
    },
    inputGroup: {
      display: 'flex',
      flexDirection: 'column'
    },
    inputGroupFull: {
      gridColumn: 'span 2'
    },
    label: {
      marginBottom: '5px',
      fontSize: '14px',
      fontWeight: '500',
      color: '#555'
    },
    input: {
      padding: '8px',
      border: '1px solid #ddd',
      borderRadius: '4px',
      fontSize: '14px'
    },
    select: {
      padding: '8px',
      border: '1px solid #ddd',
      borderRadius: '4px',
      fontSize: '14px',
      backgroundColor: 'white'
    },
    checkbox: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    button: {
      gridColumn: 'span 2',
      padding: '12px',
      backgroundColor: '#007bff',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      fontSize: '16px',
      fontWeight: '600',
      cursor: 'pointer',
      marginTop: '10px'
    },
    buttonDisabled: {
      backgroundColor: '#ccc',
      cursor: 'not-allowed'
    },
    message: {
      gridColumn: 'span 2',
      padding: '10px',
      borderRadius: '4px',
      textAlign: 'center'
    },
    success: {
      backgroundColor: '#d4edda',
      color: '#155724'
    },
    error: {
      backgroundColor: '#f8d7da',
      color: '#721c24'
    },
    tooltip: {
      marginLeft: '5px',
      cursor: 'help',
      fontSize: '14px',
      color: '#007bff'
    },
    helpText: {
      marginTop: '5px',
      padding: '10px',
      backgroundColor: '#f8f9fa',
      borderRadius: '4px',
      fontSize: '12px',
      lineHeight: '1.6',
      color: '#666'
    },
    helpList: {
      marginTop: '5px',
      marginBottom: '0',
      paddingLeft: '20px'
    }
  };

  if (!user) return null;

  return (
    <>
      <Navigation />
      <div style={styles.container}>
        <div style={styles.header}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            {user.logo_url || user.banner_url ? (
              <img
                src={(user.logo_url || user.banner_url).startsWith('http') ? (user.logo_url || user.banner_url) : `https://api.bridalconnect.co.za${user.logo_url || user.banner_url}`}
                alt="Profile"
                style={{ width: '56px', height: '56px', borderRadius: '8px', objectFit: 'cover', border: '2px solid #e0e0e0' }}
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            ) : (
              <div style={{ width: '56px', height: '56px', borderRadius: '8px', background: 'linear-gradient(135deg, #a855f7, #ec4899)', flexShrink: 0 }} />
            )}
            <div>
              <h1 style={styles.title}>Provider Dashboard</h1>
              <p style={{ margin: '5px 0 0 0', color: '#666' }}>Welcome, {user.business_name}</p>
            </div>
          </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            style={{ ...styles.logoutButton, backgroundColor: '#10b981', fontWeight: 'bold' }}
            onClick={() => navigate(`/provider/${user.id}`)}
          >
            📄 View My Profile
          </button>
          <button
            style={{ ...styles.logoutButton, backgroundColor: '#007bff' }}
            onClick={() => navigate('/media')}
          >
            Manage Media
          </button>
          <button style={styles.logoutButton} onClick={handleLogout}>
            Logout
          </button>
        </div>
      </div>

      {/* Publish Profile Banner - Only show if not published */}
      {user && profile.is_published === false && (
        <div style={{
          backgroundColor: '#FEF3C7',
          border: '3px solid #F59E0B',
          borderRadius: '12px',
          padding: '30px',
          margin: '20px auto',
          maxWidth: '1200px',
          textAlign: 'center',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '15px' }}>⚠️</div>
          <h2 style={{
            fontSize: '28px',
            fontWeight: 'bold',
            color: '#92400E',
            marginBottom: '10px'
          }}>
            Your Profile Is Not Published
          </h2>
          <p style={{
            fontSize: '16px',
            color: '#78350F',
            marginBottom: '25px',
            lineHeight: '1.6'
          }}>
            Your profile is currently in preview mode. Couples searching for wedding services cannot see your business yet.
            <br />
            Click the button below to make your profile visible to couples planning their weddings!
          </p>
          <button
            onClick={async () => {
              if (window.confirm('Are you sure you want to publish your profile? It will be visible to all couples searching for wedding services.')) {
                try {
                  await providers.publishProfile();
                  setMessage('Profile published successfully! Your business is now visible to couples.');
                  // Reload profile
                  const response = await providers.getProfile();
                  const data = response.data;
                  setProfile({
                    ...profile,
                    is_published: true,
                    ...data
                  });
                } catch (error) {
                  setMessage('Failed to publish profile. Please try again.');
                }
              }
            }}
            style={{
              backgroundColor: '#10B981',
              color: 'white',
              fontSize: '20px',
              fontWeight: 'bold',
              padding: '18px 40px',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              boxShadow: '0 4px 8px rgba(16, 185, 129, 0.3)',
              transition: 'all 0.3s'
            }}
            onMouseOver={(e) => {
              e.target.style.backgroundColor = '#059669';
              e.target.style.transform = 'scale(1.05)';
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = '#10B981';
              e.target.style.transform = 'scale(1)';
            }}
          >
            🚀 PUBLISH MY PROFILE NOW
          </button>
        </div>
      )}

      {/* Success Banner - Show if published */}
      {user && profile.is_published === true && (
        <div style={{
          backgroundColor: '#D1FAE5',
          border: '2px solid #10B981',
          borderRadius: '12px',
          padding: '20px',
          margin: '20px auto',
          maxWidth: '1200px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '32px', marginBottom: '10px' }}>✅</div>
          <h3 style={{
            fontSize: '20px',
            fontWeight: 'bold',
            color: '#065F46',
            marginBottom: '5px'
          }}>
            Your Profile Is Live!
          </h3>
          <p style={{ fontSize: '14px', color: '#047857' }}>
            Couples can now find your business when searching for wedding services.
          </p>
        </div>
      )}

      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>Business Profile</h2>

        {profileLoading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            Loading your profile...
          </div>
        ) : (
        <form style={styles.form} onSubmit={handleSubmit}>
          {message && (
            <div style={{
              ...styles.message,
              ...(message.includes('success') ? styles.success : styles.error)
            }}>
              {message}
            </div>
          )}

          {/* Tab Navigation */}
          <div style={styles.tabContainer}>
            <button
              type="button"
              style={{...styles.tab, ...(activeTab === 'basic' ? styles.activeTab : {})}}
              onClick={() => setActiveTab('basic')}
            >
              Basic Info
            </button>
            <button
              type="button"
              style={{...styles.tab, ...(activeTab === 'business' ? styles.activeTab : {})}}
              onClick={() => setActiveTab('business')}
            >
              Business Details
            </button>
            <button
              type="button"
              style={{...styles.tab, ...(activeTab === 'pricing' ? styles.activeTab : {})}}
              onClick={() => setActiveTab('pricing')}
            >
              Pricing & Services
            </button>
            <button
              type="button"
              style={{...styles.tab, ...(activeTab === 'social' ? styles.activeTab : {})}}
              onClick={() => setActiveTab('social')}
            >
              Social Media
            </button>
            <button
              type="button"
              style={{...styles.tab, ...(activeTab === 'operating' ? styles.activeTab : {})}}
              onClick={() => setActiveTab('operating')}
            >
              Operating Details
            </button>
          </div>

          {/* Basic Info Tab */}
          {activeTab === 'basic' && (
          <>
          <div style={styles.inputGroup}>
            <label style={styles.label}>Business Name</label>
            <input
              style={styles.input}
              type="text"
              name="business_name"
              value={profile.business_name}
              onChange={handleChange}
              required
            />
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>Contact Person</label>
            <input
              style={styles.input}
              type="text"
              name="contact_person"
              value={profile.contact_person}
              onChange={handleChange}
            />
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>Phone</label>
            <input
              style={styles.input}
              type="tel"
              name="phone"
              value={profile.phone}
              onChange={handleChange}
            />
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>WhatsApp Number</label>
            <input
              style={styles.input}
              type="tel"
              name="whatsapp_number"
              value={profile.whatsapp_number}
              onChange={handleChange}
              placeholder="+27..."
            />
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>Alternative Phone</label>
            <input
              style={styles.input}
              type="tel"
              name="alternative_phone"
              value={profile.alternative_phone}
              onChange={handleChange}
            />
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>Public Email (shown on profile)</label>
            <input
              style={styles.input}
              type="email"
              name="email_public"
              value={profile.email_public}
              onChange={handleChange}
              placeholder="contact@yourbusiness.com"
            />
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>Website</label>
            <input
              style={styles.input}
              type="url"
              name="website"
              value={profile.website}
              onChange={handleChange}
              placeholder="https://www.yourbusiness.com"
            />
          </div>

          {/* Google Account Linking */}
          <div style={{...styles.inputGroup, ...styles.inputGroupFull}}>
            <label style={styles.label}>Sign In Method</label>
            {user?.auth_provider === 'google' || user?.google_id ? (
              <div style={{
                padding: '12px',
                backgroundColor: '#e8f5e9',
                borderRadius: '4px',
                color: '#2e7d32',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M10 0C4.477 0 0 4.477 0 10s4.477 10 10 10 10-4.477 10-10S15.523 0 10 0zm-1 15l-5-5 1.41-1.41L9 12.17l7.59-7.59L18 6l-9 9z"/>
                </svg>
                <span style={{fontWeight: 500}}>Linked with Google Account</span>
              </div>
            ) : (
              <div>
                <p style={{fontSize: '14px', color: '#666', marginBottom: '12px'}}>
                  Link your Google account to enable easy sign-in without a password.
                </p>
                <button
                  type="button"
                  onClick={() => {
                    const apiUrl = process.env.REACT_APP_API_URL || 'https://api.bridalconnect.co.za';
                    window.location.href = `${apiUrl}/api/auth/provider/google`;
                  }}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#fff',
                    border: '2px solid #4285f4',
                    borderRadius: '4px',
                    color: '#4285f4',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 500,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 18 18">
                    <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"/>
                    <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z"/>
                    <path fill="#FBBC05" d="M3.964 10.707c-.18-.54-.282-1.117-.282-1.707 0-.591.102-1.167.282-1.707V4.961H.957C.347 6.175 0 7.55 0 9s.348 2.825.957 4.039l3.007-2.332z"/>
                    <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.961L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"/>
                  </svg>
                  Link Google Account
                </button>
              </div>
            )}
          </div>

          {/* Primary Category */}
          <div style={{...styles.inputGroup, ...styles.inputGroupFull}}>
            <label style={styles.label}>Primary Category</label>
            <select
              style={styles.select}
              name="primary_category_id"
              value={profile.primary_category_id}
              onChange={handleChange}
            >
              <option value="">Select a category</option>
              {categoryList.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
          </div>

          {/* Additional Categories */}
          {additionalCategoryPairs.length > 0 && (
            <div style={{...styles.inputGroup, ...styles.inputGroupFull}}>
              <label style={styles.label}>Additional Categories</label>
            </div>
          )}

          {additionalCategoryPairs.map((pair, index) => (
            <div key={index} style={styles.inputGroup}>
              <label style={styles.label}>Additional Category {index + 1}</label>
              <div style={{ display: 'flex', gap: '8px' }}>
                <select
                  style={{...styles.select, flex: 1}}
                  value={pair.category_id}
                  onChange={(e) => {
                    const newPairs = [...additionalCategoryPairs];
                    newPairs[index] = { category_id: e.target.value };
                    setAdditionalCategoryPairs(newPairs);
                  }}
                >
                  <option value="">Select a category</option>
                  {categoryList
                    .filter(cat => cat.id !== profile.primary_category_id)
                    .map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                </select>
                <button
                  type="button"
                  onClick={() => {
                    setAdditionalCategoryPairs(additionalCategoryPairs.filter((_, i) => i !== index));
                  }}
                  style={{
                    padding: '8px 12px',
                    backgroundColor: '#dc3545',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Remove
                </button>
              </div>
            </div>
          ))}

          <div style={{...styles.inputGroup, ...styles.inputGroupFull}}>
            <button
              type="button"
              onClick={() => {
                setAdditionalCategoryPairs([...additionalCategoryPairs, { category_id: '' }]);
              }}
              style={{
                padding: '10px 20px',
                backgroundColor: '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600'
              }}
            >
              + Add Another Category
            </button>
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>Primary City</label>
            <input
              style={styles.input}
              type="text"
              name="city"
              value={profile.city}
              onChange={handleChange}
              placeholder="e.g., Cape Town"
            />
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>Province</label>
            <select
              style={styles.select}
              name="province"
              value={profile.province}
              onChange={handleChange}
            >
              <option value="">Select a province</option>
              <option value="Eastern Cape">Eastern Cape</option>
              <option value="Free State">Free State</option>
              <option value="Gauteng">Gauteng</option>
              <option value="KwaZulu-Natal">KwaZulu-Natal</option>
              <option value="Limpopo">Limpopo</option>
              <option value="Mpumalanga">Mpumalanga</option>
              <option value="Northern Cape">Northern Cape</option>
              <option value="North West">North West</option>
              <option value="Western Cape">Western Cape</option>
            </select>
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>Postal Code</label>
            <input
              style={styles.input}
              type="text"
              name="postal_code"
              value={profile.postal_code}
              onChange={handleChange}
            />
          </div>

          {/* Additional Cities */}
          {additionalCities.length > 0 && (
            <div style={{...styles.inputGroup, ...styles.inputGroupFull}}>
              <label style={styles.label}>Additional Service Cities</label>
            </div>
          )}

          {additionalCities.map((city, index) => (
            <div key={index} style={styles.inputGroup}>
              <label style={styles.label}>City {index + 2}</label>
              <div style={{ display: 'flex', gap: '8px' }}>
                <input
                  style={{...styles.input, flex: 1}}
                  type="text"
                  value={city}
                  onChange={(e) => {
                    const newCities = [...additionalCities];
                    newCities[index] = e.target.value;
                    setAdditionalCities(newCities);
                  }}
                  placeholder="e.g., Johannesburg"
                />
                <button
                  type="button"
                  onClick={() => {
                    setAdditionalCities(additionalCities.filter((_, i) => i !== index));
                  }}
                  style={{
                    padding: '8px 12px',
                    backgroundColor: '#dc3545',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Remove
                </button>
              </div>
            </div>
          ))}

          <div style={{...styles.inputGroup, ...styles.inputGroupFull}}>
            <button
              type="button"
              onClick={() => {
                setAdditionalCities([...additionalCities, '']);
              }}
              style={{
                padding: '10px 20px',
                backgroundColor: '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600'
              }}
            >
              + Add Another City
            </button>
          </div>

          {/* Price Range Selector */}
          <div style={{...styles.inputGroup, ...styles.inputGroupFull}}>
            <label style={styles.label}>
              Price Range (Select consecutive ranges)
              <span style={{fontSize: '12px', color: '#666', marginLeft: '10px'}}>
                You can select multiple adjacent price ranges
              </span>
            </label>
            <div style={{display: 'flex', gap: '10px', flexWrap: 'wrap'}}>
              {[
                { value: '$', label: 'Budget ($)' },
                { value: '$$', label: 'Moderate ($$)' },
                { value: '$$$', label: 'Premium ($$$)' },
                { value: '$$$$', label: 'Luxury ($$$$)' }
              ].map((range, index) => {
                const priceRanges = profile.price_range || [];
                const isSelected = priceRanges.includes(range.value);

                // Check if this option should be disabled (not consecutive)
                const priceOrder = ['$', '$$', '$$$', '$$$$'];
                let isDisabled = false;
                if (priceRanges.length > 0 && !isSelected) {
                  const indices = priceRanges.map(r => priceOrder.indexOf(r));
                  const minIndex = Math.min(...indices);
                  const maxIndex = Math.max(...indices);
                  const currentIndex = priceOrder.indexOf(range.value);
                  isDisabled = currentIndex !== minIndex - 1 && currentIndex !== maxIndex + 1;
                }

                return (
                  <button
                    key={range.value}
                    type="button"
                    onClick={() => {
                      const current = Array.isArray(profile.price_range) ? [...profile.price_range] : [];
                      if (current.includes(range.value)) {
                        // Remove
                        setProfile(prev => ({
                          ...prev,
                          price_range: current.filter(r => r !== range.value)
                        }));
                      } else {
                        // Add and sort
                        const newRanges = [...current, range.value];
                        newRanges.sort((a, b) => priceOrder.indexOf(a) - priceOrder.indexOf(b));

                        // Check if consecutive
                        const indices = newRanges.map(r => priceOrder.indexOf(r));
                        const isConsecutive = indices.every((val, i, arr) =>
                          i === 0 || val === arr[i - 1] + 1
                        );

                        if (isConsecutive) {
                          setProfile(prev => ({ ...prev, price_range: newRanges }));
                        }
                      }
                    }}
                    disabled={isDisabled}
                    style={{
                      padding: '12px 20px',
                      border: isSelected ? '2px solid #7C3AED' : '2px solid #E5E7EB',
                      backgroundColor: isSelected ? '#F3E8FF' : isDisabled ? '#F3F4F6' : 'white',
                      color: isSelected ? '#7C3AED' : isDisabled ? '#9CA3AF' : '#374151',
                      borderRadius: '8px',
                      cursor: isDisabled ? 'not-allowed' : 'pointer',
                      fontWeight: isSelected ? '600' : '400',
                      fontSize: '14px',
                      transition: 'all 0.2s'
                    }}
                  >
                    {range.label}
                  </button>
                );
              })}
            </div>
            {profile.price_range && profile.price_range.length > 0 && (
              <div style={{marginTop: '8px', fontSize: '12px', color: '#10b981'}}>
                ✓ Selected: {profile.price_range.join(', ')}
              </div>
            )}
          </div>

          {/* Business Address (Optional) */}
          <div style={{...styles.inputGroup, ...styles.inputGroupFull}}>
            <label style={styles.label}>Business Address (Optional)</label>
            <input
              style={styles.input}
              type="text"
              name="business_address"
              value={profile.business_address || ''}
              onChange={handleChange}
              placeholder="123 Main Street"
            />
          </div>

          <div style={{...styles.inputGroup, ...styles.inputGroupFull}}>
            <label style={styles.label}>
              Google Reviews URL
              <button
                type="button"
                onClick={() => setShowGoogleFinder(!showGoogleFinder)}
                style={{
                  marginLeft: '10px',
                  padding: '4px 12px',
                  backgroundColor: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  fontSize: '12px',
                  cursor: 'pointer'
                }}
              >
                {showGoogleFinder ? 'Hide' : 'Find My Link'}
              </button>
            </label>
            <input
              style={styles.input}
              type="url"
              name="google_review_url"
              value={profile.google_review_url}
              onChange={handleChange}
              placeholder="https://www.google.com/maps/place/..."
            />
            {showGoogleFinder && (
              <GoogleReviewLinkFinder
                onLinkFound={(link) => {
                  setProfile(prev => ({ ...prev, google_review_url: link }));
                  setShowGoogleFinder(false);
                }}
              />
            )}
          </div>

          <div style={{...styles.inputGroup, ...styles.inputGroupFull}}>
            <label style={styles.label}>
              Facebook Page URL
              <span style={styles.tooltip} title="How to get your Facebook Page URL">
                ℹ️
              </span>
            </label>
            <input
              style={styles.input}
              type="url"
              name="facebook_review_url"
              value={profile.facebook_review_url}
              onChange={handleChange}
              placeholder="https://www.facebook.com/..."
            />
            <div style={styles.helpText}>
              <strong>How to get your Facebook Page URL:</strong>
              <ol style={styles.helpList}>
                <li>Go to your Facebook Business Page</li>
                <li>Copy the URL from your browser's address bar</li>
                <li>Or click "Share" on your page and copy the link</li>
                <li>Make sure reviews are enabled in your Page settings</li>
              </ol>
            </div>
          </div>

          <div style={styles.inputGroup}>
            <div style={styles.checkbox}>
              <input
                type="checkbox"
                id="willing_to_travel"
                name="willing_to_travel"
                checked={profile.willing_to_travel}
                onChange={handleChange}
              />
              <label htmlFor="willing_to_travel">Willing to travel</label>
            </div>
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>Travel Radius (km)</label>
            <input
              style={styles.input}
              type="number"
              name="radius_km"
              value={profile.radius_km}
              onChange={handleChange}
              min="0"
              max="500"
            />
          </div>
          </>
          )}

          {/* Business Details Tab */}
          {activeTab === 'business' && (
          <>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Description</label>
              <textarea
                style={{...styles.input, minHeight: '100px'}}
                name="description"
                value={profile.description}
                onChange={handleChange}
                placeholder="Tell us about your business..."
              />
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}>Established Year</label>
              <input
                style={styles.input}
                type="number"
                name="established_year"
                value={profile.established_year}
                onChange={handleChange}
                placeholder="2010"
              />
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}>Team Size</label>
              <input
                style={styles.input}
                type="text"
                name="team_size"
                value={profile.team_size}
                onChange={handleChange}
                placeholder="e.g., 5-10 people"
              />
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}>Years of Experience</label>
              <input
                style={styles.input}
                type="number"
                name="years_experience"
                value={profile.years_experience}
                onChange={handleChange}
              />
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}>Events Completed</label>
              <input
                style={styles.input}
                type="number"
                name="events_completed"
                value={profile.events_completed}
                onChange={handleChange}
              />
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}>Business Registration Number</label>
              <input
                style={styles.input}
                type="text"
                name="business_registration"
                value={profile.business_registration}
                onChange={handleChange}
                placeholder="e.g., 2020/123456/07"
              />
            </div>

            <div style={{...styles.inputGroup, ...styles.inputGroupFull}}>
              <label style={styles.label}>Languages Spoken</label>
              <input
                style={styles.input}
                type="text"
                name="languages_spoken"
                value={Array.isArray(profile.languages_spoken) ? profile.languages_spoken.join(', ') : profile.languages_spoken}
                onChange={(e) => setProfile(prev => ({...prev, languages_spoken: e.target.value.split(',').map(s => s.trim()).filter(Boolean)}))}
                placeholder="English, Afrikaans, Zulu"
              />
              <span style={{fontSize: '11px', color: '#888', marginTop: '4px'}}>Separate with commas</span>
            </div>

            <div style={{...styles.inputGroup, ...styles.inputGroupFull}}>
              <label style={styles.label}>Specialties</label>
              <input
                style={styles.input}
                type="text"
                name="specialties"
                value={Array.isArray(profile.specialties) ? profile.specialties.join(', ') : profile.specialties}
                onChange={(e) => setProfile(prev => ({...prev, specialties: e.target.value.split(',').map(s => s.trim()).filter(Boolean)}))}
                placeholder="e.g., Indian weddings, Outdoor ceremonies, Intimate celebrations"
              />
              <span style={{fontSize: '11px', color: '#888', marginTop: '4px'}}>Separate with commas</span>
            </div>

            <div style={{...styles.inputGroup, ...styles.inputGroupFull}}>
              <label style={styles.label}>Awards & Certifications</label>
              <input
                style={styles.input}
                type="text"
                name="awards_certifications"
                value={Array.isArray(profile.awards_certifications) ? profile.awards_certifications.join(', ') : profile.awards_certifications}
                onChange={(e) => setProfile(prev => ({...prev, awards_certifications: e.target.value.split(',').map(s => s.trim()).filter(Boolean)}))}
                placeholder="e.g., Best Venue 2024, SAACI Member"
              />
              <span style={{fontSize: '11px', color: '#888', marginTop: '4px'}}>Separate with commas</span>
            </div>

            <div style={{...styles.inputGroup, ...styles.inputGroupFull}}>
              <label style={styles.label}>Keywords (helps with search)</label>
              <input
                style={styles.input}
                type="text"
                name="keywords"
                value={Array.isArray(profile.keywords) ? profile.keywords.join(', ') : profile.keywords}
                onChange={(e) => setProfile(prev => ({...prev, keywords: e.target.value.split(',').map(s => s.trim()).filter(Boolean)}))}
                placeholder="e.g., rustic, garden, midlands, intimate"
              />
              <span style={{fontSize: '11px', color: '#888', marginTop: '4px'}}>Separate with commas</span>
            </div>
          </>
          )}

          {/* Pricing & Services Tab */}
          {activeTab === 'pricing' && (
          <>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Starting Price (ZAR)</label>
              <input
                style={styles.input}
                type="number"
                name="price_from"
                value={profile.price_from}
                onChange={handleChange}
                placeholder="e.g., 5000"
              />
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}>Maximum Price (ZAR)</label>
              <input
                style={styles.input}
                type="number"
                name="price_to"
                value={profile.price_to}
                onChange={handleChange}
                placeholder="e.g., 25000"
              />
            </div>

            <div style={{...styles.inputGroup, ...styles.inputGroupFull}}>
              <label style={styles.label}>Payment Methods Accepted</label>
              <input
                style={styles.input}
                type="text"
                name="payment_methods"
                value={Array.isArray(profile.payment_methods) ? profile.payment_methods.join(', ') : profile.payment_methods}
                onChange={(e) => setProfile(prev => ({...prev, payment_methods: e.target.value.split(',').map(s => s.trim()).filter(Boolean)}))}
                placeholder="e.g., EFT, Credit Card, Cash, SnapScan"
              />
              <span style={{fontSize: '11px', color: '#888', marginTop: '4px'}}>Separate with commas</span>
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}>Deposit Required?</label>
              <div>
                <input
                  type="checkbox"
                  id="deposit_required"
                  name="deposit_required"
                  checked={profile.deposit_required}
                  onChange={handleChange}
                />
                <label htmlFor="deposit_required" style={{marginLeft: '8px'}}>Yes, deposit required</label>
              </div>
            </div>

            {profile.deposit_required && (
              <>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Deposit Type</label>
                  <select
                    style={styles.select}
                    name="deposit_type"
                    value={profile.deposit_type || 'percentage'}
                    onChange={handleChange}
                  >
                    <option value="percentage">Percentage (%)</option>
                    <option value="fixed">Fixed Amount (ZAR)</option>
                  </select>
                </div>

                <div style={styles.inputGroup}>
                  <label style={styles.label}>
                    {profile.deposit_type === 'fixed' ? 'Deposit Amount (ZAR)' : 'Deposit Percentage (%)'}
                  </label>
                  <input
                    style={styles.input}
                    type="number"
                    name="deposit_value"
                    value={profile.deposit_value || ''}
                    onChange={handleChange}
                    min="0"
                    max={profile.deposit_type === 'percentage' ? "100" : undefined}
                    placeholder={profile.deposit_type === 'fixed' ? 'e.g., 5000' : 'e.g., 50'}
                  />
                </div>
              </>
            )}
          </>
          )}

          {/* Social Media Tab */}
          {activeTab === 'social' && (
          <>
            <div style={{...styles.inputGroup, ...styles.inputGroupFull}}>
              <label style={styles.label}>Instagram URL</label>
              <input
                style={styles.input}
                type="url"
                name="instagram_url"
                value={profile.instagram_url}
                onChange={handleChange}
                placeholder="https://instagram.com/yourhandle"
              />
            </div>

            <div style={{...styles.inputGroup, ...styles.inputGroupFull}}>
              <label style={styles.label}>TikTok URL</label>
              <input
                style={styles.input}
                type="url"
                name="tiktok_url"
                value={profile.tiktok_url}
                onChange={handleChange}
                placeholder="https://tiktok.com/@yourhandle"
              />
            </div>

            <div style={{...styles.inputGroup, ...styles.inputGroupFull}}>
              <label style={styles.label}>Facebook Profile URL</label>
              <input
                style={styles.input}
                type="url"
                name="facebook_profile_url"
                value={profile.facebook_profile_url || ''}
                onChange={handleChange}
                placeholder="https://facebook.com/yourbusiness"
              />
            </div>

            <div style={{...styles.inputGroup, ...styles.inputGroupFull}}>
              <label style={styles.label}>YouTube URL</label>
              <input
                style={styles.input}
                type="url"
                name="youtube_url"
                value={profile.youtube_url}
                onChange={handleChange}
                placeholder="https://youtube.com/@yourchannel"
              />
            </div>

            <div style={{...styles.inputGroup, ...styles.inputGroupFull}}>
              <label style={styles.label}>LinkedIn URL</label>
              <input
                style={styles.input}
                type="url"
                name="linkedin_url"
                value={profile.linkedin_url}
                onChange={handleChange}
                placeholder="https://linkedin.com/company/yourbusiness"
              />
            </div>

            <div style={{...styles.inputGroup, ...styles.inputGroupFull}}>
              <label style={styles.label}>Pinterest URL</label>
              <input
                style={styles.input}
                type="url"
                name="pinterest_url"
                value={profile.pinterest_url}
                onChange={handleChange}
                placeholder="https://pinterest.com/yourbusiness"
              />
            </div>
          </>
          )}

          {/* Operating Details Tab */}
          {activeTab === 'operating' && (
          <>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Booking Lead Time</label>
              <input
                style={styles.input}
                type="text"
                name="booking_lead_time"
                value={profile.booking_lead_time}
                onChange={handleChange}
                placeholder="e.g., 2 weeks"
              />
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}>Cancellation Policy</label>
              <textarea
                style={{...styles.input, minHeight: '80px'}}
                name="cancellation_policy"
                value={profile.cancellation_policy}
                onChange={handleChange}
              />
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}>Insurance</label>
              <div>
                <input
                  type="checkbox"
                  id="has_insurance"
                  name="has_insurance"
                  checked={profile.has_insurance}
                  onChange={handleChange}
                />
                <label htmlFor="has_insurance" style={{marginLeft: '8px'}}>We have liability insurance</label>
              </div>
            </div>

            {profile.has_insurance && (
              <div style={styles.inputGroup}>
                <label style={styles.label}>Insurance Details</label>
                <textarea
                  style={{...styles.input, minHeight: '60px'}}
                  name="insurance_details"
                  value={profile.insurance_details}
                  onChange={handleChange}
                />
              </div>
            )}

            <div style={styles.inputGroup}>
              <label style={styles.label}>Offer Free Consultation?</label>
              <div>
                <input
                  type="checkbox"
                  id="offers_consultation"
                  name="offers_consultation"
                  checked={profile.offers_consultation}
                  onChange={handleChange}
                />
                <label htmlFor="offers_consultation" style={{marginLeft: '8px'}}>Yes, we offer consultations</label>
              </div>
            </div>

            {profile.offers_consultation && (
              <div style={styles.inputGroup}>
                <label style={styles.label}>Consultation Fee (ZAR, 0 if free)</label>
                <input
                  style={styles.input}
                  type="number"
                  name="consultation_fee"
                  value={profile.consultation_fee}
                  onChange={handleChange}
                  placeholder="0"
                  min="0"
                />
              </div>
            )}

            <div style={{...styles.inputGroup, ...styles.inputGroupFull}}>
              <label style={styles.label}>Business Hours</label>
              <input
                style={styles.input}
                type="text"
                name="business_hours"
                value={typeof profile.business_hours === 'object' ? JSON.stringify(profile.business_hours) : (profile.business_hours || '')}
                onChange={handleChange}
                placeholder="e.g., Mon-Fri 8am-5pm, Sat 9am-1pm"
              />
            </div>
          </>
          )}
          <button
            style={{...styles.button, ...(loading ? styles.buttonDisabled : {}), marginTop: '30px'}}
            type="submit"
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save Profile'}
          </button>
        </form>
        )}
      </div>

      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>Profile Status</h2>
        <p>
          Verification Status:
          <strong style={{
            color: user.verification_status === 'verified' ? '#28a745' : '#ffc107',
            marginLeft: '8px'
          }}>
            {user.verification_status === 'verified' ? '✓ Verified' : '⚠ Unverified'}
          </strong>
        </p>
        {user.verification_status !== 'verified' && (
          <div style={{ color: '#666', fontSize: '14px', marginTop: '10px', lineHeight: '1.6' }}>
            <p style={{ marginBottom: '8px' }}>
              <strong>How to get verified:</strong>
            </p>
            <p style={{ marginBottom: '8px' }}>
              • Get 6 verified service providers to verify your profile (they can click the "Verify Provider" button on your profile page)
            </p>
            <p>
              • Or email <a href="mailto:info@bridalconnect.co.za" style={{ color: '#007bff', textDecoration: 'underline' }}>info@bridalconnect.co.za</a> with motivation and supporting links if you're unable to connect with 6 verified providers
            </p>
          </div>
        )}
        {user.verification_status === 'verified' && (
          <p style={{ color: '#28a745', fontSize: '14px', marginTop: '10px' }}>
            Your profile is verified! This increases your visibility and trust tier.
          </p>
        )}
      </div>
    </div>
    </>
  );
}

export default Dashboard;